import { useRef, useState } from "react";
import {
  Upload,
  X,
  FileText,
  Image,
  File as FileIcon,
  AlertCircle,
  Loader2,
  CheckCircle2,
  Download,
} from "lucide-react";
import { cn } from "@/lib/utils";

const DEFAULT_MAX_SIZE_MB = 10;
const DEFAULT_MAX_FILES = 10;
const DEFAULT_ALLOWED_MIME = [
  "image/png",
  "image/jpeg",
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

export const formatBytes = (bytes) => {
  if (!bytes) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0,
    size = bytes;
  while (size >= 1024 && i < units.length - 1) {
    size /= 1024;
    i++;
  }
  return `${size.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
};

const isRawFile = (f) => typeof File !== "undefined" && f instanceof File;
const isServerFile = (f) => !isRawFile(f) && !!f?.fileId;
const toArray = (v) => (Array.isArray(v) ? v : []);

const getFileIcon = (file) => {
  const mime = file?.type ?? file?.mimeType ?? "";
  if (mime.startsWith("image/"))
    return <Image className="h-3.5 w-3.5 text-blue-500 flex-shrink-0" />;
  if (mime === "application/pdf")
    return <FileText className="h-3.5 w-3.5 text-red-500 flex-shrink-0" />;
  return <FileIcon className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />;
};

const AttachmentInput = ({
  question = {},
  value,
  onChange,
  onUpload = null,
  onDelete = null,
  onDownload = null,
  onUploadComplete = null,
  renderActions = null,
}) => {
  const safeValue = toArray(value);

  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [errors, setErrors] = useState([]);
  const [fileStates, setFileStates] = useState({});
  // fileStates[key].status = "uploading" | "done" | "error" | "deleting"

  const accept = question.accept || ".png,.jpg,.jpeg,.pdf,.doc,.docx";
  const maxFiles = question.maxFiles ?? DEFAULT_MAX_FILES;
  const maxSizeMB = question.maxSizeMB ?? DEFAULT_MAX_SIZE_MB;
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  const allowedMime = question.allowedMime ?? DEFAULT_ALLOWED_MIME;

  const isUploading = Object.values(fileStates).some(
    (s) => s.status === "uploading",
  );
  const isDeleting = Object.values(fileStates).some(
    (s) => s.status === "deleting",
  );

  // ── Key helpers ──────────────────────────────────────────────────────────
  const fileKey = (file) =>
    file?.fileId ? `server-${file.fileId}` : `${file?.name}-${file?.size}`;

  const setFileState = (file, patch) =>
    setFileStates((prev) => ({
      ...prev,
      [fileKey(file)]: { ...(prev[fileKey(file)] ?? {}), ...patch },
    }));

  const clearFileState = (file) =>
    setFileStates((prev) => {
      const next = { ...prev };
      delete next[fileKey(file)];
      return next;
    });

  // ── Validation ───────────────────────────────────────────────────────────
  const validate = (incoming) => {
    const current = toArray(value);
    const valid = [],
      errs = [];
    for (const file of incoming) {
      if (file.size > maxSizeBytes) {
        errs.push(
          `"${file.name}" exceeds the ${maxSizeMB} MB limit (${formatBytes(file.size)})`,
        );
        continue;
      }
      if (allowedMime.length && !allowedMime.includes(file.type)) {
        errs.push(`"${file.name}" is not a supported file type`);
        continue;
      }
      if (current.length + valid.length >= maxFiles) {
        errs.push(`Maximum ${maxFiles} file(s) allowed`);
        break;
      }
      const dup = current.some(
        (f) => f?.name === file.name && f?.size === file.size,
      );
      if (!dup) valid.push(file);
    }
    return { valid, errs };
  };

  // ── Add / upload files ───────────────────────────────────────────────────
  const addFiles = async (incoming) => {
    if (!onChange) return;

    const { valid, errs } = validate(Array.from(incoming));
    setErrors(errs);
    if (!valid.length) return;

    const current = toArray(value);

    if (onUpload) {
      let workingList = [...current, ...valid];
      onChange(workingList);
      valid.forEach((f) => setFileState(f, { status: "uploading" }));

      let successCount = 0;
      for (const file of valid) {
        try {
          const uploaded = await onUpload([file]);
          const serverFile = Array.isArray(uploaded) ? uploaded[0] : uploaded;
          if (!serverFile) throw new Error("No file returned from server");

          workingList = workingList.map((item) =>
            isRawFile(item) &&
            item.name === file.name &&
            item.size === file.size
              ? serverFile
              : item,
          );

          onChange(workingList);
          clearFileState(file);
          successCount++;
        } catch (err) {
          setFileState(file, {
            status: "error",
            error: err?.message ?? "Upload failed",
          });
          setErrors((prev) => [
            ...prev,
            `"${file.name}" failed to upload. Please remove and try again.`,
          ]);
        }
      }
      if (successCount > 0) {
        onUploadComplete?.(successCount);
      }
    } else {
      onChange([...current, ...valid]);
    }
  };

  // ── Remove file ──────────────────────────────────────────────────────────
  const removeFile = async (index) => {
    if (!onChange) return;
    const file = safeValue[index];
    if (!file) return;

    // Server file with a delete handler → call API first
    if (isServerFile(file) && onDelete) {
      setFileState(file, { status: "deleting" });
      try {
        await onDelete(file.fileId);
        clearFileState(file);
        onChange(safeValue.filter((_, i) => i !== index));
      } catch (err) {
        setFileState(file, { status: "error", error: "Delete failed" });
        setErrors((prev) => [
          ...prev,
          `"${file.name}" failed to delete. Please try again.`,
        ]);
      }
      return;
    }

    // Local / raw file — just remove from list
    clearFileState(file);
    onChange(safeValue.filter((_, i) => i !== index));
  };

  // ── Download file ────────────────────────────────────────────────────────
  const handleDownload = async (file) => {
    // Raw local File → browser object URL trick
    if (isRawFile(file)) {
      const url = URL.createObjectURL(file);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.name;
      a.click();
      URL.revokeObjectURL(url);
      return;
    }
    // Server file → delegate to parent
    if (onDownload) {
      try {
        await onDownload(file);
      } catch (err) {
        console.error("Download failed:", err);
      }
    }
  };

  // ── Drag & drop / input ──────────────────────────────────────────────────
  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    addFiles(e.dataTransfer.files);
  };

  const dismissError = (i) =>
    setErrors((prev) => prev.filter((_, idx) => idx !== i));

  const busy = isUploading || isDeleting;
  const canUpload = !!onChange && !busy && safeValue.length < maxFiles;

  return (
    <div className="mt-3 space-y-2">
      {/* ── Drop zone ── */}
      <div
        onClick={() => canUpload && inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          if (canUpload) setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        className={cn(
          "flex items-center gap-3 px-4 py-3",
          "border-2 border-dashed rounded-xl text-sm transition-all duration-150",
          canUpload &&
            dragging && [
              "border-emerald-400 bg-emerald-50 text-emerald-700",
              "dark:border-emerald-500 dark:bg-emerald-950/40 dark:text-emerald-300",
            ],
          canUpload &&
            !dragging && [
              "border-gray-200 bg-gray-50 text-gray-500 cursor-pointer",
              "hover:border-emerald-400 hover:bg-emerald-50 hover:text-emerald-600",
              "dark:border-gray-700 dark:bg-gray-800/50 dark:text-gray-400",
              "dark:hover:border-emerald-500 dark:hover:bg-emerald-950/30",
            ],
          !canUpload && [
            "border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed opacity-60",
            "dark:border-gray-700 dark:bg-gray-800/30",
          ],
        )}
      >
        {/* Icon circle */}
        <div
          className={cn(
            "w-8 h-8 rounded-full border flex-shrink-0 flex items-center justify-center",
            "border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700",
          )}
        >
          {isUploading ? (
            <Loader2 className="h-4 w-4 animate-spin text-emerald-500" />
          ) : (
            <Upload className="h-4 w-4 text-gray-400 dark:text-gray-500" />
          )}
        </div>

        {/* Text */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium leading-tight">
            {isUploading ? (
              "Uploading…"
            ) : dragging ? (
              "Drop files here"
            ) : (
              <>
                <span className="text-emerald-600 dark:text-emerald-400">
                  Click to upload
                </span>
                <span className="text-gray-400 dark:text-gray-500">
                  {" "}
                  or drag & drop
                </span>
              </>
            )}
          </p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
            PNG, JPG, PDF, DOCX · Max {maxSizeMB} MB · Up to {maxFiles} file(s)
          </p>
        </div>

        {/* Counter pill */}
        {maxFiles > 1 && (
          <span
            className={cn(
              "flex-shrink-0 text-xs font-medium px-2 py-0.5 rounded-full",
              safeValue.length >= maxFiles
                ? "bg-amber-100 text-amber-600 dark:bg-amber-950/40 dark:text-amber-400"
                : "bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400",
            )}
          >
            {safeValue.length}/{maxFiles}
          </span>
        )}
      </div>

      {/* ── Hidden input ── */}
      <input
        ref={inputRef}
        type="file"
        multiple={maxFiles > 1}
        accept={accept}
        className="hidden"
        disabled={!canUpload}
        onChange={(e) => {
          addFiles(e.target.files);
          e.target.value = "";
        }}
      />

      {/* ── Errors ── */}
      {errors.length > 0 && (
        <ul className="space-y-1">
          {errors.map((err, i) => (
            <li
              key={i}
              className={cn(
                "flex items-start justify-between gap-1 rounded-lg border px-2 py-1.5",
                "bg-red-50 border-red-200 dark:bg-red-950/40 dark:border-red-800",
              )}
            >
              <div className="flex items-start gap-1.5 min-w-0">
                <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5 text-red-500 dark:text-red-400" />
                <p className="text-xs break-words text-red-600 dark:text-red-400">
                  {err}
                </p>
              </div>
              <button
                type="button"
                onClick={() => dismissError(i)}
                className="shrink-0 text-red-400 hover:text-red-600
                           dark:text-red-500 dark:hover:text-red-300"
              >
                <X className="w-3 h-3" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* ── File list ── */}
      {safeValue.length > 0 ? (
        <div className="space-y-1.5">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {safeValue.length} file{safeValue.length !== 1 ? "s" : ""} attached
          </p>

          <ul
            className={cn(
              "space-y-1 max-h-40 overflow-y-auto",
              "scrollbar-thin scrollbar-thumb-gray-200 dark:scrollbar-thumb-gray-700",
            )}
          >
            {safeValue.map((file, i) => {
              const state = fileStates[fileKey(file)];
              const uploading = state?.status === "uploading";
              const deleting = state?.status === "deleting";
              const hasError = state?.status === "error";
              const canPreview =
                isRawFile(file) && file.type?.startsWith("image/");

              // Download visible when file is ready (not mid-upload/delete)
              const showDownload =
                !uploading &&
                !deleting &&
                (isRawFile(file) || isServerFile(file));

              // Remove visible when not mid-upload/delete
              const showRemove = onChange && !uploading && !deleting;

              return (
                <li
                  key={i}
                  className={cn(
                    "flex items-center gap-2 px-2.5 py-2 rounded-lg border text-sm transition-colors",
                    hasError
                      ? "border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950/30"
                      : "border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800",
                  )}
                >
                  {/* File icon */}
                  {getFileIcon(file)}

                  {/* Name + size */}
                  <div className="flex-1 min-w-0">
                    <p className="truncate text-xs font-medium text-gray-700 dark:text-gray-200">
                      {file.name}
                    </p>
                    <p className="text-[11px] text-gray-400 dark:text-gray-500">
                      {formatBytes(file.size)}
                    </p>
                  </div>

                  {/* Image thumbnail */}
                  {canPreview && (
                    <img
                      src={URL.createObjectURL(file)}
                      alt={file.name}
                      className="h-8 w-8 rounded-md object-cover flex-shrink-0
                                 border border-gray-200 dark:border-gray-700"
                    />
                  )}

                  {/* ── Actions ── */}
                  {renderActions ? (
                    renderActions(file, i)
                  ) : (
                    <div className="flex items-center gap-1 shrink-0">
                      {/* Uploading spinner */}
                      {uploading && (
                        <Loader2 className="h-3.5 w-3.5 animate-spin text-emerald-500" />
                      )}

                      {/* Deleting spinner */}
                      {deleting && (
                        <Loader2 className="h-3.5 w-3.5 animate-spin text-red-400" />
                      )}

                      {/* Error badge */}
                      {hasError && (
                        <AlertCircle className="h-3.5 w-3.5 text-red-500" />
                      )}

                      {/* Download */}
                      {showDownload && (
                        <button
                          type="button"
                          onClick={() => handleDownload(file)}
                          aria-label={`Download ${file.name}`}
                          className={cn(
                            "p-0.5 rounded-md transition-colors",
                            "text-gray-300 hover:text-emerald-500 hover:bg-emerald-50",
                            "dark:text-gray-600 dark:hover:text-emerald-400 dark:hover:bg-emerald-950/30",
                          )}
                        >
                          <Download className="h-3.5 w-3.5" />
                        </button>
                      )}

                      {/* Remove */}
                      {showRemove && (
                        <button
                          type="button"
                          onClick={() => removeFile(i)}
                          aria-label={`Remove ${file.name}`}
                          className={cn(
                            "p-0.5 rounded-md transition-colors",
                            "text-gray-300 hover:text-red-500 hover:bg-red-50",
                            "dark:text-gray-600 dark:hover:text-red-400 dark:hover:bg-red-950/30",
                          )}
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      ) : (
        <div
          className={cn(
            "flex items-center justify-center rounded-xl border border-dashed py-4",
            "border-gray-200 dark:border-gray-700",
          )}
        >
          <p className="text-xs text-gray-400 dark:text-gray-500">
            No files attached yet
          </p>
        </div>
      )}
    </div>
  );
};

export default AttachmentInput;
